import React, { useState } from "react";
import {
  MessageCircle,
  TrendingUp,
  Target,
  Star,
  Calendar,
  User,
  Hash,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

type InsightType = {
  habits: string[];
  desires: string[];
  pain_points: string[];
};

export type EvidenceItem = {
  body: string;
  score: number;
  author: string;
  created: number;
  insights: InsightType;
  subreddit: string;
  post_title: string;
  angle_score: number;
  value_score: number;
  angle_matches: string[];
  angle_relevance: number;
  marketing_angle: string;
  relevance_score: number;
  total_score?: number;
  total_comments: number;
};

const EvidenceCommentsDisplay = ({
  evidenceComments = [],
}: {
  evidenceComments: EvidenceItem[];
}) => {
  const [expandedIndex, setExpandedIndex] = useState<null | number>(null);

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getScoreColor = (score: number) => {
    if (score >= 100) return "text-gray-900 font-semibold";
    if (score >= 70) return "text-gray-700 font-medium";
    return "text-gray-600";
  };

  const toggleExpanded = (index: null | number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <div className="mt-6 sm:mt-8 px-4 sm:px-6 lg:px-0">
      {/* Header Section - Responsive */}
      <div className="mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
          Evidence Comments
        </h2>
        <p className="text-sm sm:text-base text-gray-600">
          {evidenceComments.length} comment
          {evidenceComments.length !== 1 ? "s" : ""} found
        </p>
      </div>

      {/* Comments List - Responsive */}
      <div className="space-y-3 sm:space-y-4">
        {evidenceComments.map((comment, index) => (
          <div
            key={index}
            className="bg-white border border-gray-200 rounded-lg overflow-hidden transition-all duration-200 hover:border-gray-300 hover:shadow-sm"
          >
            {/* Header - Always Visible - Responsive */}
            <div
              className="p-3 sm:p-4 cursor-pointer select-none"
              onClick={() => toggleExpanded(index)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                  <div className="bg-gray-50 p-1.5 sm:p-2 rounded-lg flex-shrink-0 border">
                    <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base line-clamp-2 mb-1 pr-2">
                      {comment.body}
                    </h3>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs sm:text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        <span className="truncate max-w-[120px] sm:max-w-none">
                          {comment.author}
                        </span>
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(comment.created)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Score and Toggle - Responsive */}
                <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 sm:gap-3 flex-shrink-0">
                  <div className="bg-gray-50 px-2 py-1 rounded text-xs sm:text-sm font-medium text-gray-900 border whitespace-nowrap">
                    {comment.score} pts
                  </div>
                  <div className="p-1">
                    {expandedIndex === index ? (
                      <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                    )}
                  </div>
                </div>
              </div>

              {/* Scores Row - Always Visible - Responsive */}
              <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm mt-3 ml-8 sm:ml-11">
                <div className="flex items-center gap-1">
                  <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500" />
                  <span className="text-gray-600">Angle:</span>
                  <span className={`${getScoreColor(comment.angle_score)}`}>
                    {comment.angle_score?.toFixed(1)}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Star className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500" />
                  <span className="text-gray-600">Value:</span>
                  <span className={`${getScoreColor(comment.value_score)}`}>
                    {comment.value_score}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Target className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500" />
                  <span className="text-gray-600">Relevance:</span>
                  <span
                    className={`${getScoreColor(comment.relevance_score * 25)}`}
                  >
                    {comment.relevance_score}/4
                  </span>
                </div>
              </div>
            </div>

            {/* Expandable Content - Responsive */}
            <div
              className={`transition-all duration-300 ease-in-out ${
                expandedIndex === index
                  ? "max-h-screen opacity-100"
                  : "max-h-0 opacity-0"
              } overflow-hidden`}
            >
              <div className="border-t border-gray-100 p-3 sm:p-4">
                {/* Comment Body - Responsive */}
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4 border">
                  <h4 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">
                    Comment Body
                  </h4>
                  <p className="text-gray-700 text-xs sm:text-sm leading-relaxed whitespace-pre-line">
                    {comment.body}
                  </p>
                </div>

                {/* Marketing Details - Responsive Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <div className="space-y-2 sm:space-y-3">
                    {comment.marketing_angle && (
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                        <span className="text-xs sm:text-sm font-medium text-gray-600 whitespace-nowrap">
                          Marketing Angle:
                        </span>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border w-fit">
                          {comment.marketing_angle?.replace("_", " ")}
                        </span>
                      </div>
                    )}

                    {comment.angle_matches &&
                      comment.angle_matches.length > 0 && (
                        <div>
                          <span className="text-xs sm:text-sm font-medium text-gray-600 block mb-1 sm:mb-2">
                            Angle Matches:
                          </span>
                          <div className="flex flex-wrap gap-1 sm:gap-2">
                            {comment.angle_matches.map((match, idx) => (
                              <span
                                key={idx}
                                className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs border break-words"
                              >
                                {match}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>

                  {comment.angle_relevance && (
                    <div className="text-xs sm:text-sm">
                      <div className="mb-2">
                        <span className="font-medium text-gray-600">
                          Angle Relevance:
                        </span>
                        <span className="ml-2 text-gray-900 font-medium">
                          {comment.angle_relevance}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Insights - Responsive Grid */}
                {comment.insights && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {/* Pain Points */}
                    {comment.insights.pain_points &&
                      comment.insights.pain_points.length > 0 && (
                        <div className="bg-white rounded-lg p-3 border-2 border-gray-200">
                          <h5 className="font-medium text-gray-900 mb-2 text-xs sm:text-sm">
                            Pain Points
                          </h5>
                          <ul className="space-y-1">
                            {comment.insights.pain_points.map((point, idx) => (
                              <li
                                key={idx}
                                className="text-gray-700 text-xs flex items-start leading-relaxed"
                              >
                                <span className="text-gray-400 mr-1 mt-0.5 flex-shrink-0">
                                  •
                                </span>
                                <span className="break-words">{point}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                    {/* Desires */}
                    {comment.insights.desires &&
                      comment.insights.desires.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-3 border">
                          <h5 className="font-medium text-gray-900 mb-2 text-xs sm:text-sm">
                            Desires
                          </h5>
                          <ul className="space-y-1">
                            {comment.insights.desires.map((desire, idx) => (
                              <li
                                key={idx}
                                className="text-gray-700 text-xs flex items-start leading-relaxed"
                              >
                                <span className="text-gray-400 mr-1 mt-0.5 flex-shrink-0">
                                  •
                                </span>
                                <span className="break-words">{desire}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                    {/* Habits */}
                    {comment.insights.habits &&
                      comment.insights.habits.length > 0 && (
                        <div className="bg-white rounded-lg p-3 border-2 border-gray-200">
                          <h5 className="font-medium text-gray-900 mb-2 text-xs sm:text-sm">
                            Habits
                          </h5>
                          <ul className="space-y-1">
                            {comment.insights.habits.map((habit, idx) => (
                              <li
                                key={idx}
                                className="text-gray-700 text-xs flex items-start leading-relaxed"
                              >
                                <span className="text-gray-400 mr-1 mt-0.5 flex-shrink-0">
                                  •
                                </span>
                                <span className="break-words">{habit}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State - Responsive */}
      {evidenceComments.length === 0 && (
        <div className="text-center py-8 sm:py-12">
          <MessageCircle className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-3 sm:mb-4" />
          <h3 className="text-base sm:text-lg font-medium text-gray-600 mb-1 sm:mb-2">
            No Evidence Comments
          </h3>
          <p className="text-sm sm:text-base text-gray-500">
            No evidence comments available to display.
          </p>
        </div>
      )}
    </div>
  );
};

export default EvidenceCommentsDisplay;
